import React, { useState } from 'react';
import { Box, Typography, TextField, MenuItem, Snackbar, Alert } from '@mui/material';
import { useNav } from '../App';
import { C, SP, SZ } from '../theme';

const REPORT_REASONS = [
  'Inappropriate behaviour',
  'Harassment or threats',
  'Fake profile or impersonation',
  'Spam or scam',
  'Made me feel unsafe',
  'Other',
];

const S = {
  root: {
    minHeight: '100vh', background: C.bgApp,
    padding: `16px ${SP.screenH}px`,
    display: 'flex', flexDirection: 'column', gap: `${SP.md}px`,
    paddingBottom: `${SZ.navHeight + SP.xl}px`,
  },
  topBar: { display: 'flex', alignItems: 'center', gap: `${SP.md}px`, paddingBottom: `${SP.xs}px` },
  backBtn: { fontSize: '20px', cursor: 'pointer', color: C.textMuted, lineHeight: 1, padding: '2px 4px' },
  pageTitle: { fontSize: '22px', fontWeight: 600, color: C.textPrimary, letterSpacing: '-0.3px' },
  card: { background: C.bgCard, border: `0.5px solid ${C.border}`, borderRadius: '14px', padding: `${SP.cardPad}px`, display: 'flex', flexDirection: 'column', gap: `${SP.md}px` },
  subText: { fontSize: '13px', color: C.textMuted, lineHeight: 1.6 },
  submitBtn: {
    width: '100%', height: '48px', background: C.yellow, color: C.yellowText,
    border: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: 600,
    cursor: 'pointer', fontFamily: 'inherit',
  },
  submitBtnDisabled: { opacity: 0.5, cursor: 'not-allowed' },
};

const inputSx = {
  '& .MuiOutlinedInput-root': {
    background: C.bgElevated, color: C.textPrimary, fontSize: '14px', borderRadius: '10px',
    '& fieldset': { borderColor: C.border },
    '&:hover fieldset': { borderColor: C.teal },
    '&.Mui-focused fieldset': { borderColor: C.teal },
  },
  '& .MuiInputLabel-root': { color: C.textMuted, fontSize: '13px' },
  '& .MuiInputLabel-root.Mui-focused': { color: C.teal },
  '& .MuiSelect-icon': { color: C.textMuted },
};

export default function Support({ page }) {
  const navigate = useNav();
  const isReport = page === 'report';

  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [reason, setReason] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [toast, setToast] = useState({ open: false, msg: '', severity: 'success' });

  function handleSubmit() {
    if (isReport && !reason) {
      setToast({ open: true, msg: 'Please select a reason', severity: 'error' });
      return;
    }
    if (!message.trim()) {
      setToast({ open: true, msg: 'Please enter a message', severity: 'error' });
      return;
    }
    // TODO: wire to backend endpoint when email/reporting system is built
    setSubmitted(true);
  }

  if (submitted) return (
    <Box sx={S.root}>
      <Box sx={S.topBar}>
        <Box sx={S.backBtn} onClick={() => navigate('/settings')}>‹</Box>
        <Typography sx={S.pageTitle}>{isReport ? 'Report Submitted' : 'Message Sent'}</Typography>
      </Box>
      <Box sx={S.card}>
        <Typography sx={{ fontSize: '32px', textAlign: 'center' }}>
          {isReport ? '🚩' : '✅'}
        </Typography>
        <Typography sx={{ ...S.subText, textAlign: 'center' }}>
          {isReport
            ? 'Your report has been received. Our team will review it within 24 hours.'
            : 'Your message has been received. We will get back to you as soon as possible.'}
        </Typography>
        <button style={S.submitBtn} onClick={() => navigate('/settings')}>
          Back to Settings
        </button>
      </Box>
    </Box>
  );

  return (
    <Box sx={S.root}>
      <Box sx={S.topBar}>
        <Box sx={S.backBtn} onClick={() => navigate('/settings')}>‹</Box>
        <Typography sx={S.pageTitle}>{isReport ? 'Report a User' : 'Contact Support'}</Typography>
      </Box>

      <Box sx={S.card}>
        <Typography sx={S.subText}>
          {isReport
            ? 'Use this form to report a user for inappropriate behaviour. All reports are reviewed by our team.'
            : 'Have a question or issue? Send us a message and we will get back to you.'}
        </Typography>

        {!isReport && (
          <TextField fullWidth label="Your name (optional)" variant="outlined" size="small"
            value={name} onChange={e => setName(e.target.value)}
            sx={inputSx} />
        )}

        {isReport && (
          <TextField select fullWidth label="Reason for report" variant="outlined" size="small"
            value={reason} onChange={e => setReason(e.target.value)} sx={inputSx}>
            {REPORT_REASONS.map(r => (
              <MenuItem key={r} value={r}
                sx={{ fontSize: '14px', background: C.bgElevated, color: C.textPrimary }}>
                {r}
              </MenuItem>
            ))}
          </TextField>
        )}

        <TextField fullWidth label="Message" variant="outlined" size="small"
          multiline minRows={4} maxRows={8}
          value={message} onChange={e => setMessage(e.target.value)}
          inputProps={{ maxLength: 1000 }} sx={inputSx} />
        <Typography sx={{ fontSize: '11px', color: C.textDisabled, textAlign: 'right', mt: '-8px' }}>
          {message.length}/1000
        </Typography>

        <button
          style={{ ...S.submitBtn, ...(!message.trim() || (isReport && !reason) ? S.submitBtnDisabled : {}) }}
          onClick={handleSubmit}>
          {isReport ? 'Submit report' : 'Send message'}
        </button>
      </Box>

      <Snackbar open={toast.open} autoHideDuration={2500}
        onClose={() => setToast(t => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: `${SZ.navHeight + 12}px` }}>
        <Alert severity={toast.severity}
          sx={{ background: C.bgElevated, color: C.textPrimary, border: `0.5px solid ${C.border}` }}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
